import { authFetch, getAuthHeaders } from '../lib/keycloak-api';

/**
 * Base API configuration
 */
const API_BASE_URL = 'http://13.232.188.132';

const normalizeSearchResults = (payload) => {
  let rawResults;
  if (Array.isArray(payload)) {
    rawResults = payload;
  } else if (Array.isArray(payload?.results)) {
    rawResults = payload.results;
  } else if (Array.isArray(payload?.data)) {
    rawResults = payload.data;
  } else if (payload && typeof payload === 'object') {
    // Handle object with numeric keys (e.g., {0: {...}, 1: {...}, total: 5})
    // Could be top-level or inside a "data" key
    const source = (payload.data && typeof payload.data === 'object') ? payload.data : payload;
    rawResults = Object.entries(source)
      .filter(([key, val]) => /^\d+$/.test(key) && val && typeof val === 'object')
      .map(([, val]) => val);
  } else {
    rawResults = [];
  }

  return rawResults.map((result) => {
    if (!result || typeof result !== 'object') {
      return result;
    }

    const location = result.location || {};
    // Handle both nested (result.location.long) and flat (result.long) formats
    const normalizedLng = location.long ?? location.lng ?? location.lon ?? result.long ?? result.lng ?? result.lon ?? null;
    const normalizedLat = location.lat ?? result.lat ?? null;

    return {
      ...result,
      location: {
        ...location,
        lat: normalizedLat,
        lng: normalizedLng,
        long: normalizedLng
      }
    };
  });
};

/**
 * Reverse Geocoding API
 */
export const reverseGeocode = async (lat, lng, zoom) => {
  try {
    // building_visible is false if zoom <= 13, true otherwise
    const buildingVisible = zoom > 13;

    const headers = await getAuthHeaders();
    const response = await authFetch(
      `https://search-maps-pub-int.delhivery.com/reverseGeocoding?lat=${lat}&long=${lng}&building_visible=${buildingVisible}&zoom=${zoom}`,
      {
        headers: {
          ...headers,
          'accept': '*/*',
          'accept-language': 'en-GB,en-US;q=0.9,en;q=0.8',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'same-site'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Location Search API
 * Uses the main endpoint currently used on map.
 */
export const searchLocation = async (query, lat, lng) => {
  try {
    const headers = await getAuthHeaders();
    const response = await authFetch(
      `https://search-maps-pub-int.delhivery.com/search?query=${encodeURIComponent(query)}&lat=${lat}&lng=${lng}`,
      {
        headers: {
          ...headers,
          'accept': '*/*',
          'accept-language': 'en-US,en;q=0.9',
          'sec-fetch-dest': 'empty',
          'sec-fetch-mode': 'cors',
          'sec-fetch-site': 'same-site'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return normalizeSearchResults(data);
  } catch (error) {
    return []; // Return empty array instead of throwing
  }
};


/**
 * Delhivery Routing API
 */
export const getRoute = async (pointA, pointB, travelMode = 'motorcycle', stops = []) => {
  try {
    const url = 'https://dlvroute-maps-pub-int.delhivery.com/v1/route';
    
    // Build geo_coords: [source, ...stops (intermediate waypoints), destination]
    // Stops are visited in order between source and destination
    let geoCoords;
    if (stops.length > 0) {
      // Order: source → stops (in order) → destination
      geoCoords = [
        [pointA.lat, pointA.lng],
        ...stops.map(s => [s.lat, s.lng]),
        [pointB.lat, pointB.lng]
      ];
    } else {
      // No stops: just source → destination
      geoCoords = [
        [pointA.lat, pointA.lng],
        [pointB.lat, pointB.lng]
      ];
    }

    const requestBody = {
      geo_coords: geoCoords,
      travel_mode: travelMode,
      alternate_routes: true,
      output_format: {
        encode_polyline: false,
        maneuvers: true
      }
    };

    const headers = await getAuthHeaders();
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        ...headers,
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.status || !data.recommended_route) {
      throw new Error('No route found');
    }

    // Transform Delhivery API response to match OSRM format for compatibility
    const route = data.recommended_route;
    
    // Convert geometry array [[lat, lng], ...] to GeoJSON format [[lng, lat], ...]
    const coordinates = route.geometry.map(coord => [coord[1], coord[0]]);
    
    // Transform alternate routes if available
    const alternateRoutes = (data.alternates || data.alternate_routes || []).map(altRoute => {
      const altCoordinates = altRoute.geometry.map(coord => [coord[1], coord[0]]);
      
      // Extract summary from legs if available
      let altSummary = '';
      if (altRoute.legs && altRoute.legs.length > 0) {
        const leg = altRoute.legs[0];
        if (leg.maneuvers && leg.maneuvers.length > 0) {
          const streetNames = [];
          leg.maneuvers.forEach(maneuver => {
            if (maneuver.street_names && maneuver.street_names.length > 0) {
              maneuver.street_names.forEach(name => {
                if (name && !streetNames.includes(name)) {
                  streetNames.push(name);
                }
              });
            }
          });
          if (streetNames.length > 0) {
            altSummary = streetNames.slice(0, 2).join(', ');
          }
        }
      }
      
      return {
        distance: altRoute.distance * 1000, // Convert km to meters
        duration: altRoute.duration, // Already in seconds
        geometry: {
          type: 'LineString',
          coordinates: altCoordinates
        },
        legs: altRoute.legs || [],
        weight: altRoute.duration,
        weight_name: 'duration',
        summary: altSummary || altRoute.summary || ''
      };
    });
    
    return {
      distance: route.distance * 1000, // Convert km to meters
      duration: route.duration, // Already in seconds
      geometry: {
        type: 'LineString',
        coordinates: coordinates
      },
      legs: route.legs || [],
      weight: route.duration,
      weight_name: 'duration',
      alternateRoutes: alternateRoutes
    };
  } catch (error) {
    throw error;
  }
};

/**
 * POI (Points of Interest) API
 */
export const fetchPOIs = async (bbox, zoom, amenities) => {
  try {
    if (!bbox || !amenities || amenities.length === 0) {
      return { type: 'FeatureCollection', features: [] };
    }

    const amenitiesParam = amenities.join(',');
    const headers = await getAuthHeaders();
    const response = await authFetch(
      `${API_BASE_URL}/api/pois?bbox=${bbox}&zoom=${zoom}&amenities=${amenitiesParam}`,
      {
        headers: {
          ...headers,
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Roads API (for road coloring)
 */
export const fetchRoads = async (bbox, zoom) => {
  try {
    if (!bbox) {
      return { type: 'FeatureCollection', features: [] };
    }

    const headers = await getAuthHeaders();
    const response = await authFetch(
      `${API_BASE_URL}/api/roads?bbox=${bbox}&zoom=${zoom}`,
      {
        headers: {
          ...headers,
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * User-drawn features API
 */
export const fetchUserFeatures = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await authFetch(`${API_BASE_URL}/api/user-features`, {
      headers: {
        ...headers,
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return { type: 'FeatureCollection', features: [] };
  }
};

export const saveUserFeature = async (featureData) => {
  try {
    const headers = await getAuthHeaders();
    const response = await authFetch(`${API_BASE_URL}/api/user-features`, {
      method: 'POST',
      headers: {
        ...headers,
      },
      body: JSON.stringify(featureData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

export const deleteUserFeature = async (featureId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await authFetch(
      `${API_BASE_URL}/api/user-features?id=${featureId}`,
      {
        method: 'DELETE',
        headers: {
          ...headers,
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

